import { useState, useEffect } from 'react';
import { printingService, LabelType } from '../../../../../services/printingService';
import { useAuth } from '../../../../../contexts/AuthContext';

export interface UseSsccReportsReturn {
  /** Aktive rapporter av type SSCC/pall-etikett (ReportType 50). */
  reports: LabelType[];
  /** Valgt rapport-ID (default = første aktive SSCC-rapport). undefined hvis ingen finnes. */
  selectedReportId: number | undefined;
  setSelectedReportId: (reportId: number | undefined) => void;
  loading: boolean;
}

/**
 * Laster tenantens aktive SSCC-/pall-etikett-rapporter (ReportType 50) og holder
 * styr på valgt rapport. Brukes av SSCC-utskrift i plukk (pall-knappene) slik at
 * `reportId` sendes som parameter i stedet for at backend hardkoder ReportID 50.
 *
 * Ved 0 rapporter forblir selectedReportId undefined — da faller utskriften tilbake
 * til backendens innebygde standard SSCC-generator (uendret oppførsel).
 */
export function useSsccReports(): UseSsccReportsReturn {
  const { user } = useAuth();
  const [reports, setReports] = useState<LabelType[]>([]);
  const [selectedReportId, setSelectedReportId] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user?.companyNo) return;
    let cancelled = false;
    setLoading(true);
    printingService
      .getSSCCLabelTypes(user.companyNo)
      .then((list) => {
        if (cancelled) return;
        setReports(list);
        // Auto-velg første aktive SSCC-rapport hvis ingen er valgt ennå.
        setSelectedReportId((prev) => prev ?? (list.length > 0 ? list[0].reportID : undefined));
      })
      .catch((err) => {
        console.warn('Kunne ikke laste SSCC-rapporter:', err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.companyNo]);

  return { reports, selectedReportId, setSelectedReportId, loading };
}
